import * as ReactRedux from 'react-redux'
import * as React from 'react'

import * as Common from '@rushplay/common'
import * as Locks from '@rushplay/compliance/locks'
import { lazy as loadable } from '@loadable/component'

import * as Configuration from '../configuration'
import * as CombinedSelectors from '../combined-selectors'
import * as ServerConfiguration from '../server-configuration'
import * as Constants from '../constants'
import { AppFluidProviderWrapper } from '../fluid/app-fluid-provider-wrapper'
import { Chat } from '../support-chat'
import { ClipPaths } from '../clip-paths'
import { CookieConsentBanner } from '../cookie-consent-banner'
import { FluidWrapper } from '../fluid-wrapper'
import { GlobalErrorBoundary } from '../global-error-boundary'
import { GlobalStyles } from '../global-styles'
import { NotificationsContainer } from '../notifications'
import { SeoCanonicals } from '../seo-canonicals'
import { SeoHreflangs } from '../seo-hreflangs'
import { SeoMetadata } from '../seo-metadata'
import { useCookieConsent } from '../use-cookie-consent'
import { useCountryCodeListener } from '../use-configuration-listener'
import { useCssCustomProperties } from '../use-css-custom-properties'
import { useDepositEnforcing } from '../use-deposit-enforcing'
import { useGoogleDataLayerManager } from '../use-google-data-layer-manager'
import { useOfferClaimedListener } from '../use-offer-claimed-listener'
import { usePageview } from '../use-pageview'
import { usePhoneVerificationEnforcing } from '../use-phone-verification-enforcing'
import { useProfileRedirect } from '../use-profile-redirect'
import { useReturningPlayerListener } from '../use-returning-player-listener'
import { useSessionListener } from '../use-session-listener'
import { useWebSocketListener } from '../use-web-socket-listener'

import { useLocaleRedirect } from './use-locale-redirect'

const LocksContainer = loadable(() => import('./locks'))
const Primary = loadable(() => import('./primary'))
const BlockedMarket = loadable(() => import('./blocked-market'))
const Loggly = loadable(() => import('../loggly'))
const PhoneVerificationContainer = loadable(() =>
  import('./phone-verification')
)

function useAppView() {
  const { country } = ServerConfiguration.useContext()
  const hasLocks = ReactRedux.useSelector(state => Locks.hasLocks(state.locks))
  const isPhoneVerificationRequired = CombinedSelectors.useIsPhoneVerificationRequired()
  const redirected = useLocaleRedirect()

  if (redirected) {
    return null
  }

  if (country.access === 'BLOCKED') {
    return <BlockedMarket />
  }

  if (hasLocks) {
    return <LocksContainer />
  }

  if (isPhoneVerificationRequired) {
    return <PhoneVerificationContainer />
  }

  return <Primary />
}

/**
 * Topmost application component. Shall be used for setting up global
 * application capabilities and to render subapplications (main, locks,
 * language picker, etc.) based on necessary conditions.
 */
export function App() {
  useCountryCodeListener()
  useCssCustomProperties()
  useDepositEnforcing()
  usePhoneVerificationEnforcing()
  useProfileRedirect()
  useGoogleDataLayerManager()
  useOfferClaimedListener()
  usePageview()
  useReturningPlayerListener()
  useSessionListener()
  useWebSocketListener()

  const children = useAppView()
  const cookieConsentEnabled = useCookieConsent()

  const brandName = ReactRedux.useSelector(state =>
    Configuration.getBrand(state.configuration)
  )

  return (
    <GlobalErrorBoundary>
      <React.Fragment>
        <Loggly />
        <SeoMetadata />
        <SeoCanonicals />
        <SeoHreflangs />
        <ClipPaths />
        <GlobalStyles />

        {brandName === Constants.Brands.PLAYBOOM ||
        brandName === Constants.Brands.PLAYBOOM24 ? (
          <AppFluidProviderWrapper>
            <Common.Box
              display="grid"
              minHeight="fill-available"
              gridTemplateColumns={[
                // `1fr` implicitly sets minimum width to `auto` which expands
                // column to content’s size
                // `minmax(0, 1fr)` sets minimum width to `0` to shrink the column
                'minmax(0, 1fr)',
                null,
                '200px minmax(0, 1fr)',
              ]}
              gridTemplateRows="auto 1fr"
            >
              {children}
            </Common.Box>
            <FluidWrapper />
          </AppFluidProviderWrapper>
        ) : (
          <Common.Box
            display="grid"
            minHeight="fill-available"
            gridTemplateColumns={[
              // `1fr` implicitly sets minimum width to `auto` which expands
              // column to content’s size
              // `minmax(0, 1fr)` sets minimum width to `0` to shrink the column
              'minmax(0, 1fr)',
              null,
              '200px minmax(0, 1fr)',
            ]}
            gridTemplateRows="auto 1fr"
          >
            {children}
          </Common.Box>
        )}

        <NotificationsContainer />
        <Chat />
        {cookieConsentEnabled && <CookieConsentBanner />}
      </React.Fragment>
    </GlobalErrorBoundary>
  )
}
